import { useNavigate } from "@tanstack/react-router"
import type { ReactElement } from "react"
import React, { useMemo, useState } from "react"

import { CancelTemplate, ModalContainer } from "@/components/common/Modals"
import type { Contact, Location } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import AllLocationContacts from "@/screens/Companies/edit/components/locations/components/AllLocationContacts"
import PrimaryContactInfo from "@/screens/Companies/edit/components/locations/components/PrimaryContactInfo"
import { useGetContacts } from "@/screens/Companies/hooks/useGetContacts"
import SetPrimaryContact from "@/screens/Contacts/edit/components/NewPrimaryContact/SetNewPrimaryContact"
import RemovePrimaryContact from "@/screens/Contacts/edit/components/RemovePrimaryContactDialog"
import { useUpdateContactLocationPrimary } from "@/screens/Contacts/edit/hooks/useUpdateContactLocationPrimary"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

enum DialogType {
  REPLACE_PRIMARY_WARNING = "REPLACE_PRIMARY_WARNING",
  REMOVE_PRIMARY_WARNING = "REMOVE_PRIMARY_WARNING",
  SET_NEW_PRIMARY = "SET_NEW_PRIMARY",
  NO_LOCATION_CONTACTS_WARNING = "NO_LOCATION_CONTACTS_WARNING",
}

const ContactsInfo = ({
  locationData,
  companyId,
  onRefreshData,
}: {
  locationData: Location
  companyId: string
  onRefreshData?: () => void
}): ReactElement => {
  const notify = useNotificationsStore(({ enqueueNotification }) => enqueueNotification)
  const navigate = useNavigate()
  const primaryContact = locationData.primaryContact
  const locationId = locationData.locationId
  const { contactList } = useGetContacts(companyId)

  const notPrimaryLocationContacts = useMemo(
    () =>
      contactList.filter(
        (contact) =>
          contact.isActive &&
          contact.contactId !== primaryContact?.contactId &&
          contact.contactLocations.find((l) => l.location.locationId === locationId)
      ),
    [contactList, locationId, primaryContact?.contactId]
  )
  const { updateContactLocation } = useUpdateContactLocationPrimary()

  const [showDialog, setShowDialog] = useState<DialogType | null>(null)
  const [newPrimaryContact, setNewPrimaryContact] = useState<Contact | null>(null)

  const handleEditPrimaryContact = () => {
    if (notPrimaryLocationContacts.length) {
      setShowDialog(DialogType.SET_NEW_PRIMARY)
    } else {
      setShowDialog(DialogType.NO_LOCATION_CONTACTS_WARNING)
    }
  }

  const handlePrimaryContactChange = async (contactId: string | null) => {
    const newPrimaryContactData = contactList.find((contact) => contact.contactId === contactId) ?? null
    setNewPrimaryContact(newPrimaryContactData)
    // Show warning dialog if the primary contact is being replaced or removed
    if (primaryContact) {
      setShowDialog(contactId ? DialogType.REPLACE_PRIMARY_WARNING : DialogType.REMOVE_PRIMARY_WARNING)
      return
    }
    await replacePrimaryContactForLocation(newPrimaryContactData)
  }

  const updateContactLocationPrimaryStatus = async ({
    contactLocationId,
    isPrimary,
  }: {
    contactLocationId: string
    isPrimary: boolean
  }) => {
    try {
      await updateContactLocation({
        contactLocationId,
        isPrimary,
      })
    } catch (error) {
      notify({
        type: NotificationType.Error,
        message: "Error updating primary contact status",
      })
      console.error("Error updating primary contact status:", error)
    }
  }

  const removePrimaryContactForLocation = async () => {
    const contactLocationBridgeId = primaryContact?.contactLocations.find(
      (cl) => cl.locationId === locationId
    )?.contactLocationBridgeId

    if (!contactLocationBridgeId) {
      console.error("Cannot get contactLocationBridgeId")
      return
    }
    await updateContactLocationPrimaryStatus({
      contactLocationId: contactLocationBridgeId,
      isPrimary: false,
    })
    onRefreshData?.()
  }

  const replacePrimaryContactForLocation = async (newPrimaryContact: Contact | null) => {
    if (!newPrimaryContact) {
      console.error("No next contact to be set")
      return
    }
    const contactLocationBridgeId = newPrimaryContact.contactLocations.find(
      (cl) => cl.locationId === locationId
    )?.contactLocationBridgeId

    if (!contactLocationBridgeId) {
      console.error("Cannot get contactLocationBridgeId")
      return
    }

    await updateContactLocationPrimaryStatus({
      contactLocationId: contactLocationBridgeId,
      isPrimary: true,
    })

    onRefreshData?.()
    setShowDialog(null)
  }

  const goToContactsPage = async () => {
    return navigate({ to: `/app/companies/${companyId}/edit/contacts` })
  }

  return (
    <>
      <PrimaryContactInfo
        primaryContact={primaryContact ?? null}
        handleEditPrimaryContact={handleEditPrimaryContact}
        locationId={locationId}
        onRefresh={onRefreshData}
      />
      <AllLocationContacts contactList={notPrimaryLocationContacts} locationId={locationId} onRefresh={onRefreshData} />
      {showDialog === DialogType.SET_NEW_PRIMARY && (
        <SetPrimaryContact
          currentContactId={primaryContact?.contactId ?? null}
          companyId={companyId}
          location={locationData!}
          onClose={() => setShowDialog(null)}
          onConfirm={handlePrimaryContactChange}
        />
      )}
      {showDialog === DialogType.REMOVE_PRIMARY_WARNING && (
        <RemovePrimaryContact onConfirm={removePrimaryContactForLocation} onClose={() => setShowDialog(null)} />
      )}
      {showDialog === DialogType.REPLACE_PRIMARY_WARNING && newPrimaryContact && primaryContact && (
        <ModalContainer isOpen={true} onClose={() => setShowDialog(null)}>
          <CancelTemplate
            title='Are you sure you want change the primary contact for this location?'
            message={`${newPrimaryContact?.firstName} ${newPrimaryContact?.lastName} will become the primary contact in place of ${primaryContact.firstName} ${primaryContact.lastName} by continuing.`}
            confirmLabel='Proceed'
            closeLabel='Cancel'
            onClose={() => setShowDialog(null)}
            onConfirm={() => replacePrimaryContactForLocation(newPrimaryContact)}
          />
        </ModalContainer>
      )}
      {showDialog === DialogType.NO_LOCATION_CONTACTS_WARNING && (
        <ModalContainer isOpen={true} onClose={() => setShowDialog(null)}>
          <CancelTemplate
            title='There are no contacts associated with this location!'
            message='You will need to first associate a contact with this location in order to assign another primary contact'
            confirmLabel='Proceed'
            closeLabel='Cancel'
            onClose={() => setShowDialog(null)}
            onConfirm={goToContactsPage}
          />
        </ModalContainer>
      )}
    </>
  )
}

export default ContactsInfo
